import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '../auth/AuthProvider';
import { SoundboardMiniBar } from '../audio/SoundboardMiniBar';
import { SoundboardPlayerProvider } from '../audio/SoundboardPlayerProvider';
import { ToastProvider } from '../components/ui';
import { I18nProvider } from '../i18n';
import { isNativeCapacitorPlatform, installNativeBackHandler, configureOneSignal, subscribePushNavigation } from '../native/bridge';
import { installDeepLinkListener, subscribeDeepLinkNavigation } from '../native/deepLinks';
import { installEdgeSwipeBack } from '../native/edgeSwipeBack';
import { ensureProductsRegistered, initIap, type IapProduct } from '../native/iap';
import { installSizeClassAttributes } from '../layout/sizeClass';
import { installInsetBridge } from '../layout/insets';
import { PlatformProvider } from '../platform/platform';
import { SyncProvider } from '../sync/SyncProvider';
import { ThemeProvider } from '../theme/theme';
import { BetaWatermark } from './BetaWatermark';
import { BootSplash } from './BootSplash';
import { ContentSetupOverlay } from './ContentSetupOverlay';
import { CookieConsent } from './CookieConsent';
import { PageviewTracker } from './PageviewTracker';
import { OtaProvider } from './OtaProvider';
import { DiagnosticsController } from './DiagnosticsOverlay';
import { queryClient } from './queryClient';
import { installFocusGuard } from './focusGuard';
import { AppRoutes } from './routes';
import { getCoinIapPackages } from '../pages/coins/api';
import { TourHost } from '../tour/TourProvider';
import { HotkeyHost } from '../commands/HotkeyHost';
import { CommandPalette } from '../commands/CommandPalette';
import { ShortcutHelp } from '../commands/ShortcutHelp';
import { StartupGateCoordinator } from './StartupGateCoordinator';
import { ReviewPromptHost } from './ReviewPromptHost';
import { PushPrimingHost } from './PushPrimingHost';
import { CrossOnboardingHost } from './CrossOnboardingHost';
import { BestandsschutzHost } from './BestandsschutzHost';
import { GiftHost } from './GiftHost';
import { BestandsschutzEndHost } from './BestandsschutzEndHost';
import { AttributionHost } from '../attribution/AttributionHost';
import { EmailMarketingPromptHost } from './EmailMarketingPromptHost';
import { SurveyHost } from '../survey/SurveyHost';
import { MembershipRequiredHost } from './MembershipRequiredHost';
import { MembershipPromoHost } from './MembershipPromoHost';
import { MembershipOnboardingHost } from './MembershipOnboardingHost';
import { PromoProvider } from '../promo/PromoProvider';

function NativeShellBridge() {
  const navigate = useNavigate();
  // userId aus dem Auth-Kontext: aendert sich beim Login (undefined → number).
  // Wir reagieren auf den Wechsel und holen die Coin-Pakete nach, weil der
  // Boot-Fetch noch VOR dem Login lief → 401 → leere Coin-Liste.
  const { user, refresh } = useAuth();
  const userId = user?.userId;

  useEffect(() => {
    // E1: Groessenklassen- und Inset-Bruecke global installieren. Setzt
    // html[data-size]/[data-h]/[data-pointer] und den Inset-Vertrag
    // (--keyboard-inset/--kb-inset/--vh-effective/data-keyboard-open). Das
    // Inline-Script in index.html hat die Attribute schon vor dem Mount gesetzt
    // (kein Flash) — hier uebernimmt React die laufende Pflege.
    const uninstallSizeClass = installSizeClassAttributes();
    const uninstallInsets = installInsetBridge();
    // E4a: Fokus-Waechter gegen die Full-Keyboard-Access-Sackgasse in der
    // WKWebView (Apple-Forum 806077). Stellt einen aus der WebView verlorenen
    // Fokus wieder her — setzt IMMER Fokus, ruft NIE blur() (Forum 814432).
    const uninstallFocusGuard = installFocusGuard();
    // Phase 2: native shell events stay headless and route through React Router.
    installNativeBackHandler();
    // iOS-Wisch-zurueck-Geste vom linken Bildschirmrand. Auf Android/Web
    // no-op; die zentrale Back-Logik bleibt unveraendert (Modals/Stream-Guard).
    const uninstallEdgeSwipeBack = installEdgeSwipeBack();
    const recoveredOAuth = (event: Event) => {
      const detail = (event as CustomEvent<{ recovered: boolean; purpose: string; path: string }>).detail;
      if (!detail?.recovered) return;
      void (async () => {
        await refresh();
        if (detail.purpose === 'login') {
          const { getAuthMe } = await import('../api/endpoints');
          const me = await getAuthMe();
          navigate(me.profile_incomplete && me.newcomer_mode ? '/first-scene/adventure' : '/dashboard', { replace: true });
        } else if (detail.path.startsWith('/') && !detail.path.startsWith('//') && !detail.path.includes('\\')) {
          navigate(detail.path, { replace: true });
        }
      })().catch(() => undefined);
    };
    window.addEventListener('lorath:native-oauth-complete', recoveredOAuth);
    installDeepLinkListener();
    // StatusBar-Steuerung liegt vollstaendig in der Theme-Schicht: der
    // ThemeProvider hat beim Boot-Sync bereits Style + Hintergrund + Meta
    // gesetzt. Bewusst KEIN zweiter applyThemePrefs-Aufruf hier (doppelter
    // Capacitor-StatusBar-Call beim Boot = Flicker-Risiko).
    // OneSignal-Push-Client initialisieren. Fire-and-forget: auf Web/PWA und
    // ohne Plugin ist der Helper ein no-op (eigener native-guard + try/catch).
    void configureOneSignal().catch(() => undefined);
    const unsubscribeDeepLink = subscribeDeepLinkNavigation((path) => navigate(path));
    // Push-Tap → SPA-Navigation: jeder Push traegt serverseitig `data.route`
    // (z.B. /character-chat/123). subscribePushNavigation liefert async einen
    // Unsubscribe (Plugin-Resolution), inkl. gepufferter Cold-Start-Route.
    let unsubscribePush: (() => void) | null = null;
    let pushCancelled = false;
    void subscribePushNavigation((route) => navigate(route))
      .then((unsub) => { if (pushCancelled) unsub(); else unsubscribePush = unsub; })
      .catch(() => undefined);
    return () => {
      uninstallSizeClass();
      uninstallInsets();
      uninstallFocusGuard();
      uninstallEdgeSwipeBack();
      unsubscribeDeepLink();
      window.removeEventListener('lorath:native-oauth-complete', recoveredOAuth);
      pushCancelled = true;
      if (unsubscribePush) unsubscribePush();
    };
  }, [navigate, refresh]);

  // Phase 5: IAP frueh initialisieren, damit der erste Kauf-Tap nicht
  // auf den Plugin-Setup wartet. Fehler werden tolerant geloggt — die
  // UI darf NICHT blockieren, wenn der Store temporaer nicht verfuegbar
  // ist. Auf Web/PWA ist initIap ein No-Op.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (cancelled) return;
      if (!await isNativeCapacitorPlatform()) return;
      // Coin-Pakete laden — schlägt das fehl (offline/transient), darf das die
      // IAP-Init NICHT verhindern: die Abo-Produkte sind statisch und brauchen
      // keine Server-Daten. Sonst wäre der von Apple bemängelte Abo-Kauf bei
      // einem flüchtigen Coin-Endpoint-Fehler die ganze Session lang blockiert.
      let products: IapProduct[] = [];
      try {
        const response = await getCoinIapPackages();
        // HOTFIX 7.1.0.69: `pending`-Packs (z.B. Funke) NICHT beim Store registrieren.
        // Ein pending-Produkt, das in Google Play (noch) nicht sauber freigegeben ist,
        // kann die gesamte Google-Billing-Batch-Abfrage scheitern lassen → 0 Produkte
        // fuer ALLE (Coins + Abos) = „Kaeufe auf diesem Geraet nicht verfuegbar".
        // Pending-Packs werden ohnehin erst nach Store-Freigabe angezeigt.
        products = response.packs
          .filter((p) => !(p as { pending?: boolean }).pending)
          .map((p) => ({
            productId: p.id,
            coins: p.coins,
            priceEur: p.priceEur,
            name: p.name,
          }));
      } catch (err) {
        console.warn('[App] Coin-Pakete laden fehlgeschlagen — IAP wird ohne Coin-Liste initialisiert:', err);
      }
      try {
        await initIap(products);
      } catch (err) {
        console.warn('[App] IAP-Init fehlgeschlagen:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fresh-Install-Bug: Beim Boot laeuft getCoinIapPackages() VOR dem Login →
  // 401 → leere Coin-Liste → Coin-Packs werden NIE beim Store registriert.
  // Sobald der User eingeloggt ist, die Packs frisch holen und nachregistrieren
  // (ensureProductsRegistered haengt sie idempotent an den Store an, falls
  // bereits initialisiert; sonst startet es einen frischen initIap). Fehler
  // werden tolerant geloggt — ein Login-Fehler darf den IAP-Pfad NICHT werfen,
  // der Kauf-Tap faengt sich selbst ab (purchaseCoins on-demand Re-Fetch).
  // Der bestehende Boot-Effekt bleibt unveraendert, damit Abos auch ohne
  // Login sofort beim Store registriert sind.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (cancelled) return;
      if (!userId) return;
      if (!await isNativeCapacitorPlatform()) return;
      try {
        const response = await getCoinIapPackages();
        const packs: IapProduct[] = response.packs
          .filter((p) => !(p as { pending?: boolean }).pending)
          .map((p) => ({
            productId: p.id,
            coins: p.coins,
            priceEur: p.priceEur,
            name: p.name,
          }));
        await ensureProductsRegistered(packs);
      } catch (err) {
        console.warn('[App] Coin-Pakete nach Login laden fehlgeschlagen:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return null;
}

// Browser-eigene Scroll-Wiederherstellung abschalten (F-01): wir steuern das
// Scrollen bewusst selbst ueber useScrollPolicy (Aktion → oben, Uebersicht →
// gemerkte Position). 'auto' (Default) wuerde sonst dagegen arbeiten.
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

export function App() {
  return (
    <PlatformProvider>
      <ThemeProvider>
        <I18nProvider>
          {/* R6-02-d: Wasserzeichen ganz oben, oberhalb von OtaProvider und
              AuthProvider — damit es auch auf Startbildschirm, Gates und
              Fortschritts-Overlays im Bild ist. Es haengt per Portal an
              document.body und rendert nur, wenn wirklich das Beta-Bundle
              laeuft. Muss INNERHALB des I18nProvider stehen (useT). */}
          <BetaWatermark />
          <QueryClientProvider client={queryClient}>
            {/* ToastProvider muss UEBER allen Seiten liegen (useToast wird app-weit genutzt). */}
            <ToastProvider>
              {/* Klangbuehne: der Player haengt an der App-Wurzel, damit die
                  Wiedergabe KEINEN Routenwechsel mitbekommt (Julius 18.09.). */}
              <SoundboardPlayerProvider>
              {/* Phase 0: Over-the-Air-Update. Laeuft VOR Auth/Sync/Routes —
                  ein neues Web-Bundle wird eingespielt, bevor irgendein
                  Netzwerk-Call (Login, Bootstrap) startet. Native only; auf
                  Web/PWA rendert OtaProvider seine Kinder sofort. */}
              <OtaProvider>
                <AuthProvider>
                  <DiagnosticsController />
                  {/* Phase 3: headless offline sync lives below auth and above routes. */}
                  <SyncProvider>
                    {/* R5-02 (Diagnose B1-4, Befund 3): v7_startTransition ist in
                        react-router-dom 6.x per Default AUS — eine Navigation ist
                        dann ein normales State-Update, und suspendiert das Lazy-
                        Element der Zielroute, ersetzt React den GESAMTEN
                        <Routes>-Baum durch den Fallback. Mit der Option laeuft die
                        Navigation in einer Transition: die alte Seite bleibt
                        stehen, bis der Chunk da ist. Wirkt auf ALLE Routen —
                        deshalb bewusst als eigene, einzeln zurueckdrehbare Zeile. */}
                    <BrowserRouter basename="/" future={{ v7_startTransition: true }}>
                      <NativeShellBridge />
                      {/* E4a: der EINE keydown-Dispatcher (docs/30 §9.3). Muss
                          innerhalb des Routers haengen (useNavigate/useLocation). */}
                      <HotkeyHost />
                      {/* E4b: Kommandoschicht. Beide Overlays melden ihr Oeffnen-
                          Kommando (⌘K bzw. ?) beim Mount in die Registry an —
                          ohne diesen Mount waeren die Kuerzel unregistriert. */}
                      <CommandPalette />
                      <ShortcutHelp />
                      {/* Web-Analytics: Pageviews an /api/tracking (muss INNERHALB
                          des Routers liegen, useLocation) und der Consent-Banner
                          fuer Google Ads. Beides in der nativen App ein No-op —
                          Ads im App-Wrapper ist ein App-Review-Blocker. */}
                      <PageviewTracker />
                      <MembershipRequiredHost />
                      {/* W7-B (E12): Rueckfall fuer die Push-Frage — 5 Minuten
                          Nutzung oder dritter Seitenaufruf. Kopflos; das
                          Ereignis (Begleiter/Abenteuer) fragt zuerst. */}
                      <PushPrimingHost />
                      {/* P2 (60_PROMOTION.md §2): PromoProvider haelt
                          Server-State, Caps, Bündel, Chat-Sessions. Liegt
                          INNERHALB des Routers (useLocation) und oberhalb
                          des StartupGate-Coordinators, weil MembershipPromoHost
                          selbst ein Gate lesen muss. */}
                      <PromoProvider>
                        <StartupGateCoordinator>
                          {/* W7-B (E11): Cross-Onboarding. Muss INNERHALB des
                              Koordinators haengen — er liest ueber useStartupGate(),
                              ob gerade ein Gate blockiert, und schweigt dann. */}
                          <CrossOnboardingHost />
                          <BestandsschutzHost />
                          {/* Paket 75: einmaliger Hinweis zu aktivierten
                              Geschenken (wartet, solange Bestandsschutz offen ist). */}
                          <GiftHost />
                          {/* Paket A12: Hinweis „Bestandsschutz endet" (7 Tage / 1 Tag /
                              Ende-Tag vorher, je einmal). Bewusst ausserhalb des
                              Promo-Systems — ein Abschalten der Mitgliedschafts-
                              Promos laesst ihn unberuehrt. */}
                          <BestandsschutzEndHost />
                          {/* Paket A8b: einmalige, ruhige Frage nach dem E-Mail-Rückblick
                              (nur mit Double-Opt-in). Eigener Host, KEIN Teil des
                              Mitgliedschafts-Promo-Systems. */}
                          <EmailMarketingPromptHost />
                          <AttributionHost />
                          {/* P2: Modal-Dialog + Intro (innerhalb des
                              Koordinators, damit useStartupGate greift). */}
                          <MembershipPromoHost />
                          {/* S8: Onboarding nach Abo-Start (Einstiegswahl +
                              Vorteilswoche + Trial-Erinnerung). Schweigt,
                              wenn keine Mitgliedschaft existiert. */}
                          <MembershipOnboardingHost />
                          <TourHost>
                            <ReviewPromptHost />
                            <SurveyHost />
                            <AppRoutes />
                          </TourHost>
                        </StartupGateCoordinator>
                      </PromoProvider>
                      <ContentSetupOverlay />
                      {/* Klangbuehne: laeuft app-weit weiter
                          (SoundboardPlayerProvider). Die Leiste zeigt sich nur,
                          solange etwas laeuft — hier innerhalb des Routers
                          (useNavigate), aber ausserhalb der AppShell, damit sie
                          auch in den Vollbild-Chats erreichbar ist. */}
                      <SoundboardMiniBar />
                      <CookieConsent />
                      <BootSplash />
                    </BrowserRouter>
                  </SyncProvider>
                </AuthProvider>
              </OtaProvider>
              </SoundboardPlayerProvider>
            </ToastProvider>
          </QueryClientProvider>
        </I18nProvider>
      </ThemeProvider>
    </PlatformProvider>
  );
}
